import React, { useState, useEffect, useRef } from 'react';
import { Music, Search, Play, Pause, Volume2, Sparkles, X } from 'lucide-react';
import { BASE_URL } from '../lib/api';

const MusicSelector = ({ 
  script = "",
  onMusicSelect,
  selectedMusic,
  musicMode = "auto",
  onMusicModeChange,
  musicVolume = 0.3,
  onMusicVolumeChange
}) => {
  const [moods, setMoods] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [recommendedTracks, setRecommendedTracks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMood, setSelectedMood] = useState(null);
  const [playingTrackId, setPlayingTrackId] = useState(null);
  const [showBrowser, setShowBrowser] = useState(false);
  const audioRef = useRef(null);

  // Fetch moods on mount
  useEffect(() => {
    fetchMoods();
  }, []);

  // Fetch recommendations when script changes and mode is auto
  useEffect(() => {
    if (script && musicMode === "auto") {
      fetchRecommendations();
    }
  }, [script, musicMode]);

  const fetchMoods = async () => {
    try {
      const response = await fetch(`${BASE_URL}/api/music/moods`);
      const data = await response.json();
      setMoods(data.moods || []);
    } catch (error) {
      console.error("Failed to fetch moods:", error);
    }
  };

  const fetchRecommendations = async () => {
    if (!script) return;
    
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${BASE_URL}/api/music/recommend?script=${encodeURIComponent(script)}&limit=3`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json();
      if (data.success) {
        setRecommendedTracks(data.tracks || []);
      }
    } catch (error) {
      console.error("Failed to fetch recommendations:", error);
    }
  };

  const searchTracks = async (mood = null, query = "") => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      if (mood) params.append("mood", mood);
      if (query) params.append("query", query);
      params.append("limit", "10");
      
      const response = await fetch(
        `${BASE_URL}/api/music/search?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json();
      if (data.success) {
        setTracks(data.tracks || []);
      }
    } catch (error) {
      console.error("Failed to search tracks:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleMoodSelect = (mood) => {
    setSelectedMood(mood);
    searchTracks(mood, searchQuery);
  };

  const handleSearch = () => {
    searchTracks(selectedMood, searchQuery);
  };

  const playTrack = (track) => {
    if (playingTrackId === track.id) {
      // Pause
      audioRef.current?.pause();
      setPlayingTrackId(null);
    } else {
      // Play
      if (audioRef.current) {
        audioRef.current.src = track.preview_url;
        audioRef.current.play();
        setPlayingTrackId(track.id);
      }
    }
  };

  const selectTrack = (track) => {
    onMusicSelect(track);
    onMusicModeChange("manual");
    setShowBrowser(false);
  };

  const getMoodIcon = (moodId) => {
    const icons = {
      happy: "😊",
      energetic: "⚡",
      relax: "🌙",
      romantic: "❤️",
      epic: "🎬",
      inspiring: "⭐",
      upbeat: "☀️",
      chill: "☕",
      dark: "🌑"
    };
    return icons[moodId] || "🎵";
  };

  return (
    <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
      <audio ref={audioRef} onEnded={() => setPlayingTrackId(null)} />
      
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Music className="w-5 h-5 text-purple-400" />
          <h3 className="text-white font-semibold">Background Music</h3>
          <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">FREE</span>
        </div>
      </div>

      {/* Mode Selection */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => {
            onMusicModeChange("auto");
            onMusicSelect(null);
          }}
          className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
            musicMode === "auto"
              ? "bg-purple-600 text-white"
              : "bg-gray-700/50 text-gray-300 hover:bg-gray-700"
          }`}
          data-testid="music-mode-auto"
        >
          <div className="flex items-center justify-center gap-2">
            <Sparkles className="w-4 h-4" />
            <span>Auto-Match</span>
          </div>
          <p className="text-xs opacity-70 mt-1">AI picks music based on your script</p>
        </button>
        
        <button
          onClick={() => {
            onMusicModeChange("manual");
            setShowBrowser(true);
          }}
          className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
            musicMode === "manual"
              ? "bg-purple-600 text-white"
              : "bg-gray-700/50 text-gray-300 hover:bg-gray-700"
          }`}
          data-testid="music-mode-manual"
        >
          <div className="flex items-center justify-center gap-2">
            <Search className="w-4 h-4" />
            <span>Choose Music</span>
          </div>
          <p className="text-xs opacity-70 mt-1">Browse & preview tracks</p>
        </button>
        
        <button
          onClick={() => {
            onMusicModeChange("none");
            onMusicSelect(null);
          }}
          className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
            musicMode === "none"
              ? "bg-gray-600 text-white"
              : "bg-gray-700/50 text-gray-300 hover:bg-gray-700"
          }`}
          data-testid="music-mode-none"
        >
          <X className="w-4 h-4 mx-auto" />
          <p className="text-xs opacity-70 mt-1">None</p>
        </button>
      </div>

      {/* Auto Mode - Show Recommendations */}
      {musicMode === "auto" && recommendedTracks.length > 0 && (
        <div className="bg-gray-700/30 rounded-lg p-3 mb-4">
          <p className="text-xs text-gray-400 mb-2">AI will select from tracks like:</p>
          <div className="flex flex-wrap gap-2">
            {recommendedTracks.slice(0, 3).map((track) => (
              <div key={track.id} className="flex items-center gap-2 bg-gray-700/50 rounded px-2 py-1">
                <span className="text-xs text-white">{track.name}</span>
                <span className="text-xs text-gray-400">by {track.artist}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Manual Mode - Selected Track */}
      {musicMode === "manual" && selectedMusic && (
        <div className="bg-purple-500/20 border border-purple-500/30 rounded-lg p-3 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => playTrack(selectedMusic)}
                className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center hover:bg-purple-700"
              >
                {playingTrackId === selectedMusic.id ? (
                  <Pause className="w-4 h-4 text-white" />
                ) : (
                  <Play className="w-4 h-4 text-white ml-0.5" />
                )}
              </button>
              <div>
                <p className="text-white font-medium text-sm">{selectedMusic.name}</p>
                <p className="text-gray-400 text-xs">{selectedMusic.artist} • {selectedMusic.duration_formatted}</p>
              </div>
            </div>
            <button
              onClick={() => setShowBrowser(true)}
              className="text-xs text-purple-400 hover:text-purple-300"
            >
              Change
            </button>
          </div>
        </div>
      )}

      {/* Volume Control */}
      {musicMode !== "none" && (
        <div className="flex items-center gap-3">
          <Volume2 className="w-4 h-4 text-gray-400" />
          <input
            type="range"
            min="0"
            max="100"
            value={musicVolume * 100}
            onChange={(e) => onMusicVolumeChange(e.target.value / 100)}
            className="flex-1 h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
            data-testid="music-volume-slider"
          />
          <span className="text-xs text-gray-400 w-8">{Math.round(musicVolume * 100)}%</span>
        </div>
      )}

      {/* Music Browser Modal */}
      {showBrowser && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 rounded-xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            {/* Browser Header */}
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <Music className="w-5 h-5 text-purple-400" />
                Browse Music
                <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">FREE</span>
              </h3>
              <button
                onClick={() => setShowBrowser(false)}
                className="p-1 hover:bg-gray-700 rounded"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {/* Search Bar */}
            <div className="p-4 border-b border-gray-700">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                  placeholder="Search tracks..."
                  className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                />
                <button
                  onClick={handleSearch}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  <Search className="w-4 h-4" />
                </button>
              </div>

              {/* Mood Filters */}
              <div className="flex flex-wrap gap-2 mt-3">
                {moods.map((mood) => (
                  <button
                    key={mood.id}
                    onClick={() => handleMoodSelect(mood.id)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                      selectedMood === mood.id
                        ? "bg-purple-600 text-white"
                        : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                    }`}
                  >
                    {getMoodIcon(mood.id)} {mood.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Track List */}
            <div className="flex-1 overflow-y-auto p-4">
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                  <p className="text-gray-400 text-sm">Loading tracks...</p>
                </div>
              ) : tracks.length === 0 ? (
                <div className="text-center py-8">
                  <Music className="w-12 h-12 text-gray-600 mx-auto mb-2" />
                  <p className="text-gray-400">Select a mood or search to browse music</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {tracks.map((track) => (
                    <div
                      key={track.id}
                      className={`flex items-center gap-3 p-3 rounded-lg transition-all cursor-pointer ${
                        selectedMusic?.id === track.id
                          ? "bg-purple-500/20 border border-purple-500/30"
                          : "bg-gray-800/50 hover:bg-gray-800 border border-transparent"
                      }`}
                      onClick={() => selectTrack(track)}
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          playTrack(track);
                        }}
                        className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center hover:bg-purple-600 transition-colors"
                      >
                        {playingTrackId === track.id ? (
                          <Pause className="w-4 h-4 text-white" />
                        ) : (
                          <Play className="w-4 h-4 text-white ml-0.5" />
                        )}
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium text-sm truncate">{track.name}</p>
                        <p className="text-gray-400 text-xs truncate">{track.artist}</p>
                      </div>
                      <span className="text-gray-500 text-xs">{track.duration_formatted}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-700 bg-gray-800/50">
              <p className="text-xs text-gray-500 text-center">
                All tracks are royalty-free from Jamendo • Creative Commons licensed
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MusicSelector;
